import React, { useEffect, useState } from 'react';

/**
 * SplashScreen — STATIC IMAGE version (v4).
 *
 * Reason: animated versions glitched in the wild (slow 4G hid the splash
 * for some users, Google Fonts failed, `background-clip: text` made letters
 * invisible). A static PNG renders identically on every device, every time.
 *
 * Image is generated by `/app/backend/scripts/generate_splash_image.py` and
 * lives at `/splash-celesta-glow.png` (served from React's `public/` folder,
 * so it's hashed + edge-cached by the build). 1080×1920, 136 KB.
 */
const SESSION_KEY = 'cg_splash_seen_v6';
// Feb-2026: NO bundled default splash. If the admin hasn't uploaded one via
// /admin/media-tools → Splash Screen, we simply render a blank white splash
// and move on. This is the only way to guarantee the old (pre-upload)
// image never flashes on the customer's first paint.

export default function SplashScreen({ onDone }) {
  // Start with NO image so we don't flash the bundled default before the
  // admin-set splash arrives from /api/site-settings. Only when the fetch
  // completes do we render an <img> (admin's URL if set, bundled PNG as
  // fallback). This fixes the "old splash keeps showing" bug reported by
  // the merchant after they uploaded a new splash.
  const [imgSrc, setImgSrc] = useState(null);
  const [imgResolved, setImgResolved] = useState(false);
  const [visible, setVisible] = useState(() => {
    try { return !sessionStorage.getItem(SESSION_KEY); } catch (_) { return true; }
  });
  const [fadingOut, setFadingOut] = useState(false);

  // Fetch admin-set splash image. If none is set OR fetch fails we render
  // NOTHING (blank white splash). We never fall back to a bundled image —
  // that would flash the old default before the admin's upload arrives.
  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/site-settings`, { cache: 'no-store' })
      .then(r => r.json())
      .then(s => {
        if (cancelled) return;
        setImgSrc((s && s.splash_image) ? s.splash_image : null);
        setImgResolved(true);
      })
      .catch(() => {
        if (cancelled) return;
        setImgSrc(null);
        setImgResolved(true);
      });
    // Safety: don't hold the splash forever if the API is unreachable.
    const t = setTimeout(() => {
      if (cancelled) return;
      setImgResolved(true);
    }, 800);
    return () => { cancelled = true; clearTimeout(t); };
  }, [visible]);

  const finish = React.useCallback(() => {
    if (!visible || fadingOut) return;
    setFadingOut(true);
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (_) { /* noop */ }
    setTimeout(() => { setVisible(false); onDone?.(); }, 380);
  }, [visible, fadingOut, onDone]);

  // Pre-warm the homepage APIs while the splash is visible
  useEffect(() => {
    if (!visible) return;
    (async () => {
      try {
        const { cachedGet } = await import('../utils/apiCache');
        const API = process.env.REACT_APP_BACKEND_URL;
        await Promise.allSettled([
          cachedGet(`${API}/api/concerns`, { ttl: 60_000 }),
          cachedGet(`${API}/api/categories`, { ttl: 60_000 }),
          cachedGet(`${API}/api/subcategories`, { ttl: 60_000 }),
          cachedGet(`${API}/api/site-settings`, { ttl: 60_000 }),
          cachedGet(`${API}/api/combos`, { ttl: 60_000 }),
          cachedGet(`${API}/api/products?niche=anti-aging&page=1&limit=20`),
          cachedGet(`${API}/api/products?niche=skincare&page=1&limit=20`),
          cachedGet(`${API}/api/products?niche=cosmetics&page=1&limit=20`),
        ]);
      } catch (_) { /* best-effort */ }
    })();
  }, [visible]);

  useEffect(() => {
    if (!visible) { onDone?.(); return; }
    const t = setTimeout(finish, 2400);
    // 1.4 s grace before dismiss listeners arm — covers page-load scroll/touch noise
    let armed = false;
    const skip = () => { if (armed) finish(); };
    const arm = setTimeout(() => { armed = true; }, 1400);
    window.addEventListener('pointerdown', skip, { passive: true });
    window.addEventListener('keydown', skip);
    return () => {
      clearTimeout(t);
      clearTimeout(arm);
      window.removeEventListener('pointerdown', skip);
      window.removeEventListener('keydown', skip);
    };
  }, [visible, finish, onDone]);

  // Auto-dismiss immediately when there's no admin-set splash — no blank
  // white screen. If admin has uploaded a splash, we keep the standard
  // 2.4s display. If /api/site-settings fails, we also skip the splash.
  useEffect(() => {
    if (!imgResolved || !visible) return;
    if (!imgSrc) {
      // Nothing to show — skip the splash entirely (no fade, no blank white).
      try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (_) { /* noop */ }
      setVisible(false);
      onDone?.();
    }
  }, [imgResolved, imgSrc, visible, onDone]);

  if (!visible) return null;
  // Feb-2026: don't render anything until the admin-splash fetch resolves.
  // If we rendered the div immediately we'd flash a white screen for ~200ms
  // even when the API returns fast. Waiting for resolution guarantees the
  // splash only appears when there's an actual admin-uploaded image.
  if (!imgResolved) return null;
  // If admin has NO splash configured, skip the splash screen entirely
  // (the useEffect above already dismissed it).
  if (!imgSrc) return null;

  return (
    <div
      data-testid="splash-screen"
      role="status"
      aria-label="Celesta Glow"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        backgroundColor: '#ffffff',
        colorScheme: 'light',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        opacity: fadingOut ? 0 : 1,
        transition: 'opacity 380ms ease-in',
      }}
    >
      {/* Feb-2026 fix: never load the bundled default splash. The <img> only
          mounts AFTER we've resolved the admin-set splash URL from
          /api/site-settings. If the admin hasn't uploaded anything or the
          fetch fails, we render an empty white splash (brand-safe, no old
          image ever flashes). */}
      {imgResolved && imgSrc && (
        <img
          src={imgSrc}
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
          alt="Celesta Glow"
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
            opacity: 1,
            transition: 'opacity 220ms ease-in',
          }}
          decoding="async"
          fetchPriority="high"
        />
      )}
    </div>
  );
}
